import React, { useState } from 'react'
import moment from 'moment'
import { observer } from 'mobx-react'
import { runInAction } from 'mobx'
import { Link, CircularProgress } from '@material-ui/core'
import InfoOutlinedIcon from '@material-ui/icons/InfoOutlined'

import { dateFormat } from 'config/callpro.config'
import { useStores } from 'config/store'
import { CallsStore } from 'config/store/CallPro/Call/CallsStore'
import { CallDashboardTabs, CallOutcome } from 'interfaces/Call.interface'

import { LinearProgress } from '../LinearProgress'
import { ChartCard } from 'components/CallPro/ChartCard'
import CustomTooltip from '../CustomTooltip/CustomTooltip'
import { useStyles } from './AnsweredBy.style'
import CallDetailsDrawer from 'components/CallPro/SideDrawer/CallDetailsDrawer/CallDetailsDrawer'
import AnsweredByDrawer from '../SideDrawer/AllAnsweredBy/AllAnsweredByDrawer'
import EmptyBarChart from '../EmptyCharts/EmptyBarChart'

interface AnsweredByInterface {
  isAccordionContent?: boolean
}

type AnsweredByItem = CallsStore['answeredBy']['records'][number]

const AnsweredBy = (props: AnsweredByInterface) => {
  const { isAccordionContent } = props
  const { callsStore, dashboardUIStore } = useStores()
  const classes = useStyles()
  const { answeredBy: _answeredBy, loadingStates } = callsStore
  const answeredBy = _answeredBy.records
  const firstRecordDate = _answeredBy.firstRecordDate

  const total = answeredBy.reduce(
    (accumulator, currentElement) => accumulator + currentElement.value,
    0
  )
  const [drawerOpen, setDrawerOpen] = useState(false)

  const handleToggleDrawer = () => setDrawerOpen((value) => !value)

  const handleItemClick = (item: AnsweredByItem) => () => {
    runInAction(() => {
      const tab = CallDashboardTabs.ALL_CALLS
      const outcome = CallOutcome.ALL_CALLS
      dashboardUIStore.setCurrentTabbedOpened(tab)
      callsStore.setFilters({ outcome, answeredBy: [item.name] })
      callsStore.setPaginationPage(1)
      callsStore.refreshAllCallsTab()
    })
  }

  // TOOLTIP CONTENT
  const tooltipContent = (
    <div className={classes.metricTooltip}>
      <div className={classes.tooltipTitle}>Click to see the full list</div>
    </div>
  )

  return (
    <>
      <ChartCard
        headerTitle="CALL ANSWERED BY"
        tooltipText="This shows if your calls were answered by staff, IVR, or voicemail."
        headerTotalText="Total"
        headerTotalValue={total}
        isAccordionContent={isAccordionContent}
      >
        {loadingStates.answeredBy ? (
          <div
            style={{
              textAlign: 'center',
              padding: '2rem',
            }}
          >
            <CircularProgress />
          </div>
        ) : answeredBy.length > 0 ? (
          <>
            {answeredBy.slice(0, 5).map((item, key) => {
              return (
                <LinearProgress
                  key={`item-${key}`}
                  value={item.value}
                  total={total}
                  label={
                    <Link
                      component="button"
                      variant="body1"
                      underline="none"
                      color="initial"
                      onClick={handleItemClick(item)}
                      classes={{ root: classes.link }}
                    >
                      {item.name}
                    </Link>
                  }
                />
              )
            })}

            <div className={classes.footer}>
              <div className={classes.container}>
                <div className={classes.infoContainer}>
                  <div className={classes.info}>
                    <CustomTooltip content={tooltipContent}>
                      <div
                        className={classes.infoDetail}
                        onClick={handleToggleDrawer}
                      >
                        All Call Answered By ({_answeredBy.records.length})
                      </div>
                    </CustomTooltip>
                  </div>
                </div>
              </div>
              {firstRecordDate.length ? (
                <div className={classes.disclaimer}>
                  <InfoOutlinedIcon className={classes.disclaimerIcon} />
                  The data used in this graph is derived from the calls we
                  scored since {moment(firstRecordDate).format(dateFormat)}.
                </div>
              ) : null}
            </div>
          </>
        ) : (
          <EmptyBarChart />
        )}
      </ChartCard>
      <CallDetailsDrawer
        drawerOpen={drawerOpen}
        handleCloseDrawer={handleToggleDrawer}
        title="All Call Answered By"
      >
        <AnsweredByDrawer />
      </CallDetailsDrawer>
    </>
  )
}

export default observer(AnsweredBy)
